import {useEffect, useState} from "react";
import {fetchSegmentsData} from "../helper/requests";

export interface IPhoto {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number;
  height: number;
  formats: {
    thumbnail?: {
      ext: string;
      url: string;
      hash: string;
      mime: string;
      name: string;
      path: string | null;
      size: number;
      width: number;
      height: number;
      sizeInBytes: number;
    };
    small?: {
      ext: string;
      url: string;
      hash: string;
      mime: string;
      name: string;
      path: string | null;
      size: number;
      width: number;
      height: number;
      sizeInBytes: number;
    };
    medium?: {
      ext: string;
      url: string;
      hash: string;
      mime: string;
      name: string;
      path: string | null;
      size: number;
      width: number;
      height: number;
      sizeInBytes: number;
    };
    large?: {
      ext: string;
      url: string;
      hash: string;
      mime: string;
      name: string;
      path: string | null;
      size: number;
      width: number;
      height: number;
      sizeInBytes: number;
    };
  };
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

export interface IEmployee {
  id: number;
  documentId: string;
  fullName: string;
  department: string | null;
  phoneNumber: string | null;
  email: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  jobFunction: string | null;
  interactionLevel: string | null;
  photo?: IPhoto;
}

export interface IInitiative {
  id: number;
  documentId: string;
  name: string;
  description: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  lifecycle_stage: string | null;
  publication_date: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
  effective_milestones: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
  company_deadlines: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
  planned_updates: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
  obligation_level: string | null;
  business_impact: string | null;
  noncompliance_risks: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
  has_benefits: boolean | null;
  benefits_desc: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
  direct_costs: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
  roi_expected_for_voluntary: boolean | null;
  participation_formats: string | null;
  lobbying_opportunity: string | null;
  sources: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }> | null;
}

export interface ILogo {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number;
  height: number;
  formats: any;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

export interface IOrganization {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  employees: IEmployee[];
  initiatives: IInitiative[];
}

export interface IOrganizationSegment {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  organizations: IOrganization[];
}

export interface IOrganizationSegmentsResponse {
  data: IOrganizationSegment[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface IOrganizationFromSegment extends IOrganization {
  segment: string; // Название сегмента
}

export interface IAuthoritiesFromSegmentsResponse {
  data: IOrganizationFromSegment[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface IInitiativeDetailed {
  id: number;
  documentId: string;
  name: string;
  description: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  lifecycle_stage?: string;
  publication_date?: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
  effective_milestones?: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
  company_deadlines?: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
  planned_updates?: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
  obligation_level?: string;
  business_impact?: string;
  noncompliance_risks?: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
  has_benefits?: boolean;
  benefits_desc?: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
  direct_costs?: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
  roi_expected_for_voluntary?: boolean;
  participation_formats?: string;
  lobbying_opportunity?: string;
  sources?: Array<{
    type: string;
    children: Array<{
      text: string;
      type: string;
    }>;
  }>;
}

export interface IAuthority {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  logo: ILogo | null;
  comments: any[];
  employees: IEmployee[];
  initiatives: IInitiativeDetailed[];
}

export interface IInitiativeBasic {
  id: number;
  documentId: string;
  name: string;
}

export interface IAuthoritiesResponse {
  data: IAuthority[];
  meta: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

const useFetchDataCrgciAuthorities = (
  apiUrl: string | undefined,
  apiKey: string | undefined
) => {
  const [authoritiesData, setAuthoritiesData] =
    useState<IAuthoritiesFromSegmentsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiUrl || !apiKey) {
      setError("API URL или ключ не предоставлены");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchSegmentsData(apiUrl, apiKey);

        if (data) {
          const authoritiesFromSegments: IAuthoritiesFromSegmentsResponse = {
            data: data.data.flatMap((segment: IOrganizationSegment) =>
              segment.organizations.map((org: IOrganization) => ({
                ...org,
                segment: segment.name,
              }))
            ),
            meta: data.meta,
          };
          setAuthoritiesData(authoritiesFromSegments);
        } else {
          setError("Не удалось загрузить данные организаций");
        }
      } catch (err) {
        console.error("Error in useFetchDataCrgciAuthorities:", err);
        setError("Ошибка при загрузке данных организаций");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, apiKey]);

  return {authoritiesData, loading, error};
};

export default useFetchDataCrgciAuthorities;
